/**
 * The race, as far as it can be decided without a database.
 *
 * Points say who is ahead. Prizes say what being ahead is *for*, which is the
 * part a six-year-old actually runs at.
 */

export const PRIZE_KINDS = ["everyone", "race"] as const;
export type PrizeKind = (typeof PRIZE_KINDS)[number];

export const AUDIENCES = ["kid", "parent", "guest", "all"] as const;
export type Audience = (typeof AUDIENCES)[number];

export interface Prize {
  id: number;
  points: number;
  kind: PrizeKind;
  place: number | null;
  title: string;
  emoji: string;
  audience: Audience;
}

export interface AwardedPrize {
  prizeId: number;
  personId: number;
  place: number | null;
  awardedAt: string;
}

/** A prize as a lane marker: where it sits, and whether it is still winnable. */
export interface Marker extends Prize {
  /** Everyone who has taken it this week, in the order they got there. */
  winners: Array<{ personId: number; place: number | null }>;
  /** A race prize with every place taken is decided; nobody else can reach it. */
  taken: boolean;
}

export function isPrizeKind(value: unknown): value is PrizeKind {
  return typeof value === "string" && (PRIZE_KINDS as readonly string[]).includes(value);
}

export function isAudience(value: unknown): value is Audience {
  return typeof value === "string" && (AUDIENCES as readonly string[]).includes(value);
}

export function appliesTo(prize: { audience: Audience }, kind: "parent" | "kid" | "guest"): boolean {
  return prize.audience === "all" || prize.audience === kind;
}

/**
 * One race is all the places at the same line, for the same audience.
 *
 * First, second and third are three rows but a single contest; without that
 * grouping the first runner past the line takes the whole podium.
 */
function raceKey(prize: Prize): string {
  return `${prize.points}|${prize.audience}`;
}

/**
 * The prizes a person has newly earned at a given score.
 *
 * Pure so the awarding rule can be argued with in a test rather than inferred
 * from SQL. `alreadyWon` is what they hold this week; `placesTaken` is how many
 * of each prize row have gone, whoever took them.
 */
export function newlyEarned(
  points: number,
  prizes: Prize[],
  alreadyWon: Set<number>,
  placesTaken: Map<number, number>,
): Array<{ prize: Prize; place: number | null }> {
  const earned: Array<{ prize: Prize; place: number | null }> = [];

  // Races this person is already in, either from earlier in the week or from
  // a line they crossed a moment ago in this same call.
  const placed = new Set(prizes.filter((prize) => alreadyWon.has(prize.id)).map(raceKey));

  for (const prize of prizes) {
    if (points < prize.points) continue;
    if (alreadyWon.has(prize.id)) continue;

    if (prize.kind === "race") {
      // You get one place per race, not the whole podium: crossing the line
      // once cannot make you first *and* second *and* third.
      if (placed.has(raceKey(prize))) continue;
      if ((placesTaken.get(prize.id) ?? 0) > 0) continue;
      placed.add(raceKey(prize));
      earned.push({ prize, place: prize.place });
    } else {
      earned.push({ prize, place: null });
    }
  }

  return earned;
}

/**
 * How far to the next thing worth running at.
 *
 * Only counts prizes still winnable by this person: a first place somebody else
 * already took is not a finish line, it is scenery.
 */
export function nextMarker(points: number, markers: Marker[], personId: number): Marker | null {
  return (
    markers
      .filter((marker) => marker.points > points)
      .filter((marker) => !marker.taken)
      .filter((marker) => !marker.winners.some((winner) => winner.personId === personId))
      .sort((a, b) => a.points - b.points)[0] ?? null
  );
}

/** The furthest anyone has to run this week — the length of the track. */
export function trackLength(markers: Marker[], scores: number[]): number {
  return Math.max(...markers.map((marker) => marker.points), ...scores, 1);
}
